import { Box } from '@mui/joy';
import { CellValueChangedEvent } from 'ag-grid-community';
import { useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { ProblemNumber } from 'bom/elements';
import { SolveProblemsStep } from 'bom/enums';
import { useBomImportActions, useGetUomList } from 'bom/hooks';
import { useBomPartsImportContext } from 'bom/states';
import { IUomListPayload, IUomListRow, IUomTableRow } from 'bom/types';
import { mapSaveUomsData } from 'bom/utils';
import { Button, Notification } from 'core/components';
import { NotificationCard, Title } from 'core/elements';
import { NotificationCardType, NotificationPosition } from 'core/enums';
import { AgGridClientTableProvider, useInnerStepsContext, useStepsContext } from 'core/states';
import { GetManyResponse } from 'core/types';

import { getColumnDefs } from './columns';
import { Table } from './Table';

interface IProps {
  isValid: boolean;
}

export const PartsWithInvalidUom = ({ isValid }: IProps) => {
  const [mappedRecords, setMappedRecords] = useState<IUomTableRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  // Row edits mutate in place (no setState), so bump to re-render and re-check the submit gate.
  const [, bumpEdit] = useReducer((value) => value + 1, 0);

  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { activeStep } = useInnerStepsContext();
  // Last inner step → advances the OUTER wizard (Solve Problems -> Preview).
  const { goToNextStep } = useStepsContext();
  const { partImportId, problems, setProblems } = useBomPartsImportContext();

  const allValid = useMemo(
    () => isValid || problems[SolveProblemsStep.MEASURE_CONFLICT]?.unsolved === 0,
    [isValid, problems]
  );

  const payload: IUomListPayload = useMemo(() => ({ partImport: partImportId }), [partImportId]);

  const enabled = useMemo(
    () => !isValid && activeStep?.type === SolveProblemsStep.MEASURE_CONFLICT,
    [isValid, activeStep]
  );

  const {
    records,
    isLoading: isDataLoading,
    loadingPercentage,
    currentPage,
  } = useGetUomList(payload, enabled, (data: GetManyResponse<IUomListRow>) => {
    const records = data.records || [];

    // Recompute from the freshly-fetched list (re-runs after each submit). `unsolved` is the truth
    // (rows the BE still flags); `solved` accumulates how many have dropped out.
    setProblems((prev) => {
      const current = prev[SolveProblemsStep.MEASURE_CONFLICT];
      const prevUnsolved = current?.unsolved ?? records.length;
      const prevSolved = current?.solved ?? 0;

      return {
        ...prev,
        [SolveProblemsStep.MEASURE_CONFLICT]: {
          solved: prevSolved + (prevUnsolved - records.length),
          unsolved: records.length,
        },
      };
    });
  });

  const { saveUoms } = useBomImportActions();

  // Seed the editable rows per fetch (initial load + after each submit's re-fetch). Between fetches
  // the rows are local: the user picks UOMs and adjusts quantities, then submits the batch.
  useEffect(() => {
    if (isDataLoading) {
      return;
    }

    setMappedRecords(
      records.map((record) => ({
        id: record?.id,
        number: record?.number,
        manufacturer: record?.manufacturer,
        description: record?.description,
        uom: record?.uom,
        uomValidationStatus: record?.uomValidationStatus,
        quantity: record?.quantity,
        assignedUom: null,
        allowedUoms: record?.allowedUoms ?? [],
        adjustedQuantity: record?.quantity,
        omit: false,
      }))
    );
  }, [isDataLoading, records]);

  useEffect(() => {
    if (!allValid) {
      return;
    }

    const timer = setTimeout(() => goToNextStep(), 1000);

    return () => clearTimeout(timer);
  }, [allValid, goToNextStep]);

  const defaultColDef = useMemo(
    () => ({
      wrapHeaderText: true,
      autoHeaderHeight: true,
      suppressMovable: true,
      enableRowGroup: false,
      maxWidth: 300,
    }),
    []
  );

  const columnDefs = useMemo(() => getColumnDefs(), []);

  // Edits commit to the row in place — `data` is the same object as the `mappedRecords` entry.
  // Returning no `updatedRow` stops the provider from cloning the row, so several edits on one row
  // (set the UOM, then adjust the quantity) all accumulate and are read together on submit.
  const onCellValueChanged = (_, { colDef: { field }, data, newValue }: CellValueChangedEvent) => {
    data[field] = newValue;

    // A UOM choice or omit toggle changes whether the row is resolved → refresh the submit gate.
    if ('assignedUom' === field || 'omit' === field) {
      bumpEdit();
    }

    return {};
  };

  const onSubmit = async () => {
    setIsSaving(true);

    const { success } = await saveUoms(mapSaveUomsData(mappedRecords, partImportId));

    if (!success) {
      setIsSaving(false);

      return;
    }

    // Re-fetch so the BE re-classifies: a chosen base UOM may still mismatch this part. Resolved
    // rows drop out; rows still flagged are re-seeded for another pass.
    await queryClient.invalidateQueries('bomImportUomList');

    setIsSaving(false);
  };

  const content =
    isDataLoading || allValid ? (
      <>
        <Title text={t('titleMeasureConflict')} />

        <NotificationCard
          title={isDataLoading ? t('titleStatus') : t('titleSuccess')}
          message={isDataLoading ? t('messageUomsValidating') : t('messageUomsValidated')}
          type={isDataLoading ? NotificationCardType.LOADING : NotificationCardType.SUCCESS}
        />
      </>
    ) : (
      <>
        <Title
          text={t('titleMeasureConflict')}
          subtitle={
            <>
              <ProblemNumber
                number={
                  problems[SolveProblemsStep.MEASURE_CONFLICT]?.unsolved || mappedRecords.length
                }
              />
              <ProblemNumber
                number={problems[SolveProblemsStep.MEASURE_CONFLICT]?.solved || 0}
                solved
              />
            </>
          }
        />

        <Notification position={NotificationPosition.DRAWER} />

        <AgGridClientTableProvider
          tableId="partsWithInvalidUom"
          entityType="Part"
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          data={mappedRecords}
          isDataLoading={isDataLoading}
          onCellValueChanged={onCellValueChanged}
          loadingPercentage={loadingPercentage}
          dataCurrentPage={currentPage}
          sideBar={{}}
          autoSizeStrategy={{
            type: 'fitCellContents',
          }}
          headerCheckbox={!isDataLoading}
        >
          <Table onRowsChanged={bumpEdit} />
        </AgGridClientTableProvider>
      </>
    );

  // Gate submit until every flagged row is resolved: a UOM chosen or the row omitted.
  const allRowsResolved = mappedRecords.every(
    (row) => Boolean(row.assignedUom) || Boolean(row.omit)
  );

  return (
    <>
      {content}

      <Box>
        <Button
          text={allValid ? t('buttonLabelGoToTheNextStep') : t('buttonLabelSubmitInvalidUoms')}
          onClick={allValid ? () => goToNextStep() : onSubmit}
          disabled={isDataLoading || isSaving || (!allValid && !allRowsResolved)}
        />
      </Box>
    </>
  );
};
